'use client';

import { forwardRef, type MouseEvent } from 'react';
import { Button } from '@/shared/ui/shadcn/button';
import type { ButtonProps } from './Button';
import { LoadingButton } from './LoadingButton';
import { useButtonProtection } from '@/shared/lib/hooks/useButtonProtection';
export interface ProtectedButtonProps extends ButtonProps {
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void | Promise<void>;
  timeout?: number;
  onTimeout?: () => void;
  loadingText?: string;
  showLoadingState?: boolean;
}

export const ProtectedButton = forwardRef<
  HTMLButtonElement,
  ProtectedButtonProps
>(
  (
    {
      onClick,
      timeout = 10000,
      onTimeout,
      disabled,
      children,
      loadingText,
      showLoadingState = true,
      ...props
    },
    ref
  ) => {
    const { isProtected, executeWithProtection } = useButtonProtection({
      timeout,
      onTimeout,
    });

    const handleClick = async (e: MouseEvent<HTMLButtonElement>) => {
      if (!onClick || isProtected) return;

      try {
        await executeWithProtection(async () => {
          await onClick(e);
        });
      } catch (error) {
        if (
          error instanceof Error &&
          error.message === 'Action already in progress'
        ) {
          return;
        }
        throw error;
      }
    };

    if (showLoadingState && isProtected) {
      return (
        <LoadingButton
          {...props}
          ref={ref}
          disabled={true}
          isLoading={true}
          loadingText={
            loadingText || (typeof children === 'string' ? children : undefined)
          }
          onClick={handleClick}
        >
          {children}
        </LoadingButton>
      );
    }

    return (
      <Button
        ref={ref}
        disabled={disabled || isProtected}
        onClick={handleClick}
        {...props}
      >
        {children}
      </Button>
    );
  }
);

ProtectedButton.displayName = 'ProtectedButton';
